import { spotArea, spotAt, type CalloutEdit, type MapId } from "@69ly/maps";
import type { Combo, MemberPosition, Stuff } from "@69ly/shared";
import { useMemo } from "react";
import { useCallouts } from "./callouts.ts";
import { PERSONAL, useCurrentTeam } from "./current-team.tsx";
import { usePositions } from "./positions.ts";
import { useMe } from "./queries.ts";

export type SpotSide = "t" | "ct" | "both";

/**
 * "From my spot" (0.27.0): my spots on a map from Team › Who plays where (main and
 * backups), and the area they make with the callouts next to them. Only a team has
 * positions: in the personal library there is none.
 */
export interface MySpots {
  /** The positions are still loading. */
  loading: boolean;
  /** My spots' names on this side ("A Site", "Jungle"); both sides together for "both". */
  names: (side: SpotSide) => string[];
  /** Where I play on this side, as spots ("upper|base:Jungle#0"); null without a spot. */
  area: (side: SpotSide) => Set<string> | null;
  /** The admins' callout changes on this map (where a throw is depends on them). */
  edits: readonly CalloutEdit[];
}

const sidesOf = (side: SpotSide): ("t" | "ct")[] => (side === "both" ? ["t", "ct"] : [side]);

/** My positions on a map, for one side or both. */
function mine(positions: MemberPosition[], me: string | undefined, map: MapId, side: SpotSide) {
  return positions.filter((p) => p.userId === me && p.map === map && sidesOf(side).includes(p.side));
}

export function useMySpots(map: MapId | null): MySpots {
  const { scope } = useCurrentTeam();
  const teamId = scope && scope !== PERSONAL ? scope : null;
  const positions = usePositions(teamId);
  const me = useMe().data?.user?.id;
  const callouts = useCallouts();
  return useMemo(() => {
    const edits = map ? callouts.edits(map) : [];
    const list = positions.data ?? [];
    const names = (side: SpotSide) => {
      if (!map) return [];
      const out: string[] = [];
      for (const p of mine(list, me, map, side)) for (const n of [p.spot, ...p.backups.map((b) => b.name)]) if (n && !out.includes(n)) out.push(n);
      return out;
    };
    const area = (side: SpotSide) => {
      if (!map) return null;
      const spots = mine(list, me, map, side)
        .flatMap((p) => [p.spotKey, ...p.backups.map((b) => b.key)])
        .filter((k): k is string => !!k);
      return spots.length ? spotArea(map, spots, edits) : null;
    };
    return { loading: !!teamId && positions.isPending, names, area, edits };
  }, [map, positions.data, positions.isPending, me, callouts, teamId]);
}

/** Thrown from inside an area: the callout the player stands on is one of its spots. */
export function thrownFrom(map: MapId, s: Pick<Stuff, "throw">, area: Set<string>, edits: readonly CalloutEdit[]): boolean {
  const spot = spotAt(map, s.throw.x, s.throw.y, s.throw.z, edits);
  return !!spot && area.has(spot);
}

/** A combo is mine when one of its throws is: I may well be the one throwing it. */
export function comboFrom(map: MapId, c: Pick<Combo, "steps">, area: Set<string>, edits: readonly CalloutEdit[]): boolean {
  return c.steps.some((st) => thrownFrom(map, st.stuff, area, edits));
}
